import { type MouseEvent } from 'react';
import * as tracking from 'dibs-tracking';

/**
 * Track search within results
 */
export function trackSearchRefine(): void {
    tracking.trackEvent({
        category: 'results page refinement',
        action: 'search within results',
    });
}

/**
 * Track when a filter is selected / deselected
 */
export function trackFilterClick({
    facetType,
    filter,
    checked,
}: {
    facetType: string;
    filter: string;
    checked: boolean;
}): void {
    const state = checked ? 'selected' : 'deselected';
    tracking.trackEvent({
        category: `results page facet ${state}`,
        action: `${facetType} ${state}`,
        label: filter,
    });
}

/**
 * Track expand or collapse of facet when clicking caret
 */
export function trackFacetClick({
    facetType,
    isExpanded,
}: {
    facetType: string;
    isExpanded: boolean;
}): void {
    const state = isExpanded ? 'collapse' : 'expand';
    tracking.trackEvent({
        category: 'results page refinement',
        action: `${state} facet`,
        label: facetType,
    });
}

/**
 * Track when additional filter values are expanded or hidden when clicking facet link at bottom
 */
export function trackFacetLinkClick({
    facetType,
    isExpanded,
}: {
    facetType: string;
    isExpanded: boolean;
}): void {
    const state = isExpanded ? 'fewer' : 'more';
    tracking.trackEvent({
        category: 'results page refinement',
        action: `show ${state} options`,
        label: facetType,
    });
}

/**
 * Track when breadcrumb is clicked
 */
export function trackBreadcrumbClick(
    { breadcrumb }: { breadcrumb: string },
    event: MouseEvent | null = null
): void {
    tracking.trackEvent(
        {
            category: 'navigation',
            action: 'breadcrumb click',
            label: `Browse-photos|${breadcrumb}`,
        },
        event
    );
}

/**
 * Track when pagination links are clicked
 */
export function trackPaginationClick({ ratio }: { ratio: string }): void {
    tracking.trackEvent(
        {
            category: 'navigation',
            action: 'pagination page number click',
            label: ratio,
            isInteractiveEvent: true,
        },
        null
    );
}

/**
 * Track when photo is clicked to open gallery modal
 */
export function trackPhotoClick(): void {
    tracking.trackEvent({
        category: 'trade firm project image interaction',
        action: 'gallery opened',
        label: 'browse-photos',
    });
}

const DATA_LAYER_SETTINGS = Object.freeze({
    pageType: 'Other',
    pageLevel: 'Other',
    pageSubType: 'Trade Firm',

    countOfItems: 0,
    ecommerce: undefined,
});

export function trackPhotoVPVPageView({
    firmUrlLabel,
    projectName,
}: {
    firmUrlLabel: string;
    projectName: string;
}): void {
    const project = projectName
        .replace(/\s+/g, '-') // replace spaces with '-' for vpv
        .toLowerCase(); // lower case for vpv

    // update dataLayer object (before page view!)
    tracking.addGTMMessage(DATA_LAYER_SETTINGS);

    // fire pageview
    tracking.trackPageview({
        url: `/designers/${firmUrlLabel}/projects/${project}/photos/`,
        additional: { countOfItems: '0' },
    });
}
